'use client';

import { c as _c } from "react/compiler-runtime";
import type { Address } from 'viem';
import { ChainUtils } from '@morpho-org/blue-sdk';
import { Avatar, ClipboardButton, DynamicAvatarsStack, type DynamicAvatarsStackAlign } from '@morpho-org/ui-v6/client';
import { useMediaQuery } from '@morpho-org/ui-v6/hooks';
import { ExternalLink, Tag } from '@morpho-org/ui-v6/server';
import { cn } from '@morpho-org/ui-v6/utils';
import { useNow } from '@repo/hooks';
import { Popover, PopoverAnchor, PopoverContent, Separator, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { ArrowDiagonalUp, WarningTriangleSolid } from '@repo/ui/icons';
import { abbreviateAddress } from '@repo/utils';
import { Fragment, type KeyboardEvent, type ReactNode, useRef, useState } from 'react';
import { BottomSheet } from '@/components/common/BottomSheet';
import { ButtonLink } from '@/components/common/ButtonLink';
import UserCellExplorer from '@/components/common/Cells/UserCell/UserCellExplorer';
import { TextAddress } from '@/components/common/TextAddress';
import type { FeedSideGroup } from './groupBySide';
import { EMPTY, type FeedRow, formatAge, formatDuration, formatFeedValue, formatUpdates, formatUtcTime, getProviderUrl, isStale, type OracleAdminSummary, type Provider, SIDE_LABEL, typeBadgeLabel } from './oracleFeeds';
const PROVIDER_LOGO: Record<string, string> = {
  accountable: '/assets/oracles/accountable.svg',
  api3: '/assets/oracles/api3.png',
  apyx: '/assets/oracles/apyx.png',
  avant: '/assets/oracles/avant.png',
  chainlink: '/assets/oracles/chainlink.png',
  chronicle: '/assets/oracles/chronicle.png',
  compound: '/assets/oracles/compound.svg',
  eoracle: '/assets/oracles/eoracle.png',
  ethena: '/assets/oracles/ethena.svg',
  etherfi: '/assets/oracles/etherfi.png',
  fx: '/assets/oracles/fx.svg',
  infinifi: '/assets/oracles/infinifi.png',
  jane3: '/assets/oracles/jane3.png',
  lido: '/assets/oracles/lido.png',
  maple: '/assets/oracles/maple.svg',
  midas: '/assets/oracles/midas.svg',
  ojo: '/assets/oracles/ojo.png',
  origami: '/assets/oracles/origami.png',
  pareto: '/assets/oracles/pareto.png',
  pendle: '/assets/oracles/pendle.png',
  pyth: '/assets/oracles/pyth.png',
  re: '/assets/oracles/re.svg',
  redstone: '/assets/oracles/redstone.png',
  reservoir: '/assets/oracles/reservoir.png',
  sky: '/assets/oracles/sky.png',
  soter: '/assets/oracles/soter.png',
  spark: '/assets/oracles/spark.png',
  steakhouse: '/assets/oracles/steakhouse.svg',
  stork: '/assets/oracles/stork.png',
  upshift: '/assets/oracles/upshift.png',
  wildcat: '/assets/oracles/wildcat.png',
  yearn: '/assets/oracles/yearn.svg'
};
export const TOUCH_QUERY = '(hover: none)';
export function Muted(t0) {
  const $ = _c(2);
  const {
    children: t1
  } = t0;
  const children = t1 === undefined ? EMPTY : t1;
  let t2;
  if ($[0] !== children) {
    t2 = <span className="text-secondary-foreground">{children}</span>;
    $[0] = children;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  return t2;
}

/** A table link, built like the Liquidator column's `UserCellExplorer`: label then ↗, hover pill. */
export function CellLink(t0) {
  const $ = _c(12);
  const {
    href,
    children,
    logo,
    shrink: t1
  } = t0;
  const shrink = t1 === undefined ? false : t1;
  const t2 = shrink && "min-w-0";
  let t3;
  if ($[0] !== t2) {
    t3 = cn("-mx-sm inline-flex", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = shrink ? "min-w-0 shrink" : undefined;
  let t5;
  if ($[2] !== logo) {
    t5 = logo !== undefined ? {
      src: logo,
      rounded: true
    } : undefined;
    $[2] = logo;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== children || $[5] !== t4 || $[6] !== t5) {
    t6 = {
      variant: "ghost",
      size: "default",
      className: t4,
      label: children,
      avatarProps: t5
    };
    $[4] = children;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== href || $[9] !== t3 || $[10] !== t6) {
    t7 = <ButtonLink href={href} mode="external" className={t3} buttonProps={t6} />;
    $[8] = href;
    $[9] = t3;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
}
export const providerLogo = (provider: Provider) => provider.logo ?? PROVIDER_LOGO[provider.id] ?? null;

/** Logo, or the placeholder avatar when there's none, and name as plain text. */
export function ProviderName(t0) {
  const $ = _c(9);
  const {
    provider
  } = t0;
  let t1;
  if ($[0] !== provider) {
    t1 = providerLogo(provider);
    $[0] = provider;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <Avatar src={t1} size={16} padding={0} rounded={true} aria-hidden={true} />;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== provider.name) {
    t3 = <span className="truncate">{provider.name}</span>;
    $[4] = provider.name;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <span className="gap-2xs flex min-w-0 items-center">{t2}{t3}</span>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}

/** Logo and name; link to the feed page or provider site when there is one. */
export function ProviderLabel(t0) {
  const $ = _c(11);
  const {
    feed
  } = t0;
  const {
    provider
  } = feed;
  if (!provider) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Muted />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== feed) {
    t1 = getProviderUrl(feed);
    $[1] = feed;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const href = t1;
  if (!href) {
    let t2;
    if ($[3] !== provider) {
      t2 = <ProviderName provider={provider} />;
      $[3] = provider;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  let t2;
  if ($[5] !== provider) {
    t2 = providerLogo(provider);
    $[5] = provider;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== href || $[8] !== provider.name || $[9] !== t2) {
    t3 = <CellLink href={href} logo={t2}>{provider.name}</CellLink>;
    $[7] = href;
    $[8] = provider.name;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}

/** Logo and name as plain text, then the ↗ button to the provider's site. */
export function ProviderAttribute(t0) {
  const $ = _c(10);
  const {
    provider
  } = t0;
  let t1;
  if ($[0] !== provider) {
    t1 = getProviderUrl({
      provider,
      feedUrl: null
    });
    $[0] = provider;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const href = t1;
  let t2;
  if ($[2] !== provider) {
    t2 = <ProviderName provider={provider} />;
    $[2] = provider;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== href || $[5] !== provider) {
    t3 = href && <Tooltip><TooltipTrigger asChild={true}><div className="flex"><ButtonLink href={href} className="flex" buttonProps={{
            variant: "ghost",
            size: "sm",
            iconRight: undefined,
            iconAlone: <><ArrowDiagonalUp className="text-secondary-foreground" /><span className="sr-only">{`Go to ${provider.name}`}</span></>
          }} /></div></TooltipTrigger><TooltipContent>{`Go to ${provider.name}.`}</TooltipContent></Tooltip>;
    $[4] = href;
    $[5] = provider;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <span className="gap-2xs flex h-[25.6px] items-center">{t2}{t3}</span>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}

/** A link in running text: the text's own colour, underlined, no icon. */
export function InlineExternalLink(t0) {
  const $ = _c(3);
  const {
    href,
    children
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== href) {
    t1 = <ExternalLink href={href} className="text-inherit underline underline-offset-2">{children}</ExternalLink>;
    $[0] = children;
    $[1] = href;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}

/**
 * `Last updated 5m ago · Reported as-is by Chainlink, Pendle`, credited under each feed table.
 * "Last updated" is the decoding's snapshot time (`observed_at`, when the API read the feeds), not
 * any feed's own update. Its age counts from now, while rows show each feed's age at the snapshot;
 * hovering it shows the UTC time.
 * The server and hydration renders have no clock, so they leave the age out rather than show 0.
 */
export function Attribution(t0) {
  const $ = _c(12);
  const {
    providers,
    observedAt,
    className
  } = t0;
  const now = useNow(60000);
  let t1;
  if ($[0] !== className) {
    t1 = cn("text-muted-foreground text-2xs", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== now || $[3] !== observedAt) {
    t2 = now !== undefined && <>Last updated{" "}<Hint title="Last updated" content={formatUtcTime(observedAt)} underline={false}><time dateTime={new Date(observedAt * 1000).toISOString()}>{formatAge(Math.max(0, Math.floor(now / 1000) - observedAt))} ago</time></Hint></>;
    $[2] = now;
    $[3] = observedAt;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = now !== undefined && providers.length > 0 && " \xB7 ";
  let t4;
  if ($[5] !== providers) {
    t4 = providers.length > 0 && <>Reported as-is by{" "}{providers.map(_temp)}</>;
    $[5] = providers;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t1 || $[8] !== t2 || $[9] !== t3 || $[10] !== t4) {
    t5 = <p className={t1}>{t2}{t3}{t4}</p>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}

/**
 * The whole label links to `address` on the chain's block explorer. The negative
 * margin cancels the button padding so the text lines up with plain cells.
 */
function _temp(provider, index) {
  const href = getProviderUrl({
    provider,
    feedUrl: null
  });
  return <Fragment key={provider.id}>{index > 0 && ", "}{href ? <InlineExternalLink href={href}>{provider.name}</InlineExternalLink> : provider.name}</Fragment>;
}
export function ExplorerLink(t0) {
  const $ = _c(15);
  const {
    chainId,
    address,
    label,
    readContract: t1,
    shrink: t2
  } = t0;
  const readContract = t1 === undefined ? false : t1;
  const shrink = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== address || $[1] !== chainId) {
    t3 = ChainUtils.getExplorerAddressUrl(chainId, address);
    $[0] = address;
    $[1] = chainId;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const href = t3;
  let t4;
  if ($[3] !== address || $[4] !== label) {
    t4 = label ?? abbreviateAddress(address);
    $[3] = address;
    $[4] = label;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const text = t4;
  if (!href) {
    const t5 = shrink && "flex min-w-0";
    let t6;
    if ($[6] !== t5) {
      t6 = cn(t5);
      $[6] = t5;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    let t7;
    if ($[8] !== t6 || $[9] !== text) {
      t7 = <span className={t6}>{text}</span>;
      $[8] = t6;
      $[9] = text;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    return t7;
  }
  const t5 = readContract ? `${href}#readContract` : href;
  let t6;
  if ($[11] !== shrink || $[12] !== t5 || $[13] !== text) {
    t6 = <CellLink href={t5} shrink={shrink}>{text}</CellLink>;
    $[11] = shrink;
    $[12] = t5;
    $[13] = text;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}

/** An admin address, as the Liquidator column shows it: avatar, short address, ↗. */
function AdminLink(t0) {
  const $ = _c(3);
  const {
    chainId,
    address
  } = t0;
  let t1;
  if ($[0] !== address || $[1] !== chainId) {
    t1 = <span className="-mx-sm inline-flex"><UserCellExplorer address={address} chainId={chainId} /></span>;
    $[0] = address;
    $[1] = chainId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}

/**
 * The feed's name, linking to its contract on the block explorer. A leg with no feed links to the
 * oracle's read-contract tab instead, where its empty slot is visible.
 */
export function FeedName(t0) {
  const $ = _c(22);
  const {
    chainId,
    row
  } = t0;
  const [ref, truncated] = useIsTruncated();
  let t1;
  if ($[0] !== ref || $[1] !== row.name) {
    t1 = <span ref={ref} className="min-w-0 truncate">{row.name}</span>;
    $[0] = ref;
    $[1] = row.name;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const label = t1;
  const link = row.address ?? row.proof?.oracle;
  let t2;
  if ($[3] !== chainId || $[4] !== label || $[5] !== link || $[6] !== row.address) {
    t2 = link ? <ExplorerLink chainId={chainId} address={link} label={label} readContract={!row.address} shrink={true} /> : label;
    $[3] = chainId;
    $[4] = label;
    $[5] = link;
    $[6] = row.address;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const name = t2;
  const listedAs = hasListedAs(row) ? `Listed as ${row.listedAs}` : null;
  if (!truncated && !listedAs) {
    let t3;
    if ($[8] !== name) {
      t3 = <span className="flex min-w-0">{name}</span>;
      $[8] = name;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    return t3;
  }
  const t3 = link ? undefined : 0;
  let t4;
  if ($[10] !== name || $[11] !== t3) {
    t4 = <TooltipTrigger asChild={true}><span className="flex min-w-0" tabIndex={t3}>{name}</span></TooltipTrigger>;
    $[10] = name;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== listedAs || $[14] !== row.name || $[15] !== truncated) {
    t5 = truncated ? <span className="flex flex-col"><span>{row.name}</span>{listedAs && <span className="text-secondary-foreground">{listedAs}</span>}</span> : listedAs;
    $[13] = listedAs;
    $[14] = row.name;
    $[15] = truncated;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== t5) {
    t6 = <TooltipContent side="top">{t5}</TooltipContent>;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t4 || $[20] !== t6) {
    t7 = <Tooltip>{t4}{t6}</Tooltip>;
    $[19] = t4;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
}

/** Whether the row's visible name replaced a different declared name. */
export const hasListedAs = (row: FeedRow) => !!row.listedAs && row.listedAs !== EMPTY && row.listedAs !== row.name;

/** A ref for a `truncate` element and whether its text is clipped, rechecked on resize. */
export function useIsTruncated() {
  const $ = _c(3);
  const [truncated, setTruncated] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = element => {
      if (!element) {
        return;
      }
      const measure = () => setTruncated(element.scrollWidth - element.clientWidth > 1);
      measure();
      if (typeof ResizeObserver === "undefined") {
        return;
      }
      const observer = new ResizeObserver(measure);
      observer.observe(element);
      return () => observer.disconnect();
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const ref = t0;
  let t1;
  if ($[1] !== truncated) {
    t1 = [ref, truncated];
    $[1] = truncated;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1 as const;
}

/** Hover tooltip on pointer devices, a bottom sheet on touch screens. */
export function Hint(t0) {
  const $ = _c(24);
  const {
    title,
    content,
    label,
    underline: t1,
    children
  } = t0;
  const underline = t1 === undefined ? true : t1;
  const touch = useMediaQuery(TOUCH_QUERY);
  const [open, setOpen] = useState(false);
  if (touch) {
    const t2 = underline && "underline decoration-dotted underline-offset-2";
    let t3;
    if ($[0] !== t2) {
      t3 = cn("text-left", t2);
      $[0] = t2;
      $[1] = t3;
    } else {
      t3 = $[1];
    }
    let t4;
    let t5;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = event => {
        event.stopPropagation();
        setOpen(true);
      };
      t5 = event_0 => {
        if (event_0.key !== "Enter" && event_0.key !== " ") {
          return;
        }
        event_0.preventDefault();
        event_0.stopPropagation();
        setOpen(true);
      };
      $[2] = t4;
      $[3] = t5;
    } else {
      t4 = $[2];
      t5 = $[3];
    }
    let t6;
    if ($[4] !== children || $[5] !== label || $[6] !== t3) {
      t6 = <span role="button" tabIndex={0} aria-label={label} className={t3} onClick={t4} onKeyDown={t5}>{children}</span>;
      $[4] = children;
      $[5] = label;
      $[6] = t3;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    let t7;
    if ($[8] !== content || $[9] !== open || $[10] !== title) {
      t7 = <BottomSheet open={open} onOpenChange={setOpen} title={title}>{content}</BottomSheet>;
      $[8] = content;
      $[9] = open;
      $[10] = title;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    let t8;
    if ($[12] !== t6 || $[13] !== t7) {
      t8 = <>{t6}{t7}</>;
      $[12] = t6;
      $[13] = t7;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    return t8;
  }
  const t2 = label ? "button" : undefined;
  let t3;
  if ($[15] !== children || $[16] !== label || $[17] !== t2) {
    t3 = <TooltipTrigger asChild={true}><span tabIndex={0} role={t2} aria-label={label} className="inline-flex cursor-help">{children}</span></TooltipTrigger>;
    $[15] = children;
    $[16] = label;
    $[17] = t2;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  let t4;
  if ($[19] !== content) {
    t4 = <TooltipContent>{content}</TooltipContent>;
    $[19] = content;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] !== t3 || $[22] !== t4) {
    t5 = <Tooltip>{t3}{t4}</Tooltip>;
    $[21] = t3;
    $[22] = t4;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  return t5;
}

/** The Oracle contract row's address with the admin avatar: avatar, short address, copy, ↗. */
function AddressWithCopy(t0) {
  const $ = _c(8);
  const {
    chainId,
    address
  } = t0;
  let t1;
  if ($[0] !== address) {
    t1 = <Avatar address={address} size="small" aria-hidden={true} />;
    $[0] = address;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== address || $[3] !== chainId) {
    t2 = <TextAddress textClassName="text-xs" address={address} targetChainId={chainId} toastMessage="Address copied to clipboard." tooltipMessage="Copy address to clipboard." textColorClass="text-foreground" />;
    $[2] = address;
    $[3] = chainId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <span className="gap-2xs inline-flex items-center">{t1}{t2}</span>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}

/** `Unknown` when the API couldn't resolve it, `None` when it confirms there's none, else the addresses. */
// TODO(cash): return multisig threshold / owners for an admin that is a Safe.
export function AdminValue(t0) {
  const $ = _c(23);
  const {
    chainId,
    admins,
    withCopy: t1,
    align: t2
  } = t0;
  const withCopy = t1 === undefined ? false : t1;
  const align = t2 === undefined ? "left" : t2;
  if (admins === null) {
    let t3;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <Muted>Unknown</Muted>;
      $[0] = t3;
    } else {
      t3 = $[0];
    }
    return t3;
  }
  const known = Array.isArray(admins) ? admins : admins.known;
  let t3;
  if ($[1] !== admins) {
    t3 = Array.isArray(admins) ? [] : admins.unknown;
    $[1] = admins;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const unknown = t3;
  const [only] = known;
  let t4;
  if ($[3] !== align || $[4] !== chainId || $[5] !== known || $[6] !== only || $[7] !== withCopy) {
    t4 = known.length === 1 && only ? withCopy ? <AddressWithCopy chainId={chainId} address={only} /> : <AdminLink chainId={chainId} address={only} /> : known.length > 1 ? <AdminStack chainId={chainId} admins={known} align={align} /> : <span>None</span>;
    $[3] = align;
    $[4] = chainId;
    $[5] = known;
    $[6] = only;
    $[7] = withCopy;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const knownValue = t4;
  let t5;
  if ($[9] !== known.length || $[10] !== knownValue || $[11] !== unknown.length) {
    t5 = unknown.length > 0 && known.length === 0 ? <Muted>Unknown</Muted> : knownValue;
    $[9] = known.length;
    $[10] = knownValue;
    $[11] = unknown.length;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const value = t5;
  if (unknown.length === 0) {
    return knownValue;
  }
  let t6;
  if ($[13] !== unknown) {
    t6 = unknown.map(_temp2);
    $[13] = unknown;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t6) {
    t7 = <span className="flex flex-col">{t6}</span>;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <WarningTriangleSolid aria-hidden={true} className="text-warning-foreground size-3.5 shrink-0" />;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t7) {
    t9 = <Hint title="Admin" content={t7} label="Admin unknown, show details" underline={false}>{t8}</Hint>;
    $[18] = t7;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t9 || $[21] !== value) {
    t10 = <span className="gap-2xs inline-flex items-center">{value}{t9}</span>;
    $[20] = t9;
    $[21] = value;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
}

/** Same count as the Trusted by and Providers stacks. */
function _temp2(name, index) {
  return <span key={`${name}-${index}`}>Admin unknown: {name}</span>;
}
const MAX_VISIBLE_AVATARS = 10;

/**
 * The Trusted by avatar stack, one blockie per admin. Each avatar opens a card with its address,
 * copy and ↗; "+N" lists the hidden ones. Touch screens open a bottom sheet instead.
 */
function AdminStack(t0) {
  const $ = _c(18);
  const {
    chainId,
    admins,
    align
  } = t0;
  let t1;
  if ($[0] !== chainId) {
    t1 = addresses => addresses.map(address => <AdminCardRow key={address} chainId={chainId} address={address} />);
    $[0] = chainId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const toRows = t1;
  let t2;
  if ($[2] !== admins) {
    t2 = admins.slice(MAX_VISIBLE_AVATARS);
    $[2] = admins;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const hidden = t2;
  let t3;
  if ($[4] !== admins || $[5] !== chainId || $[6] !== toRows) {
    let t4;
    if ($[8] !== chainId || $[9] !== toRows) {
      t4 = address_0 => ({
        logoUrl: null,
        address: address_0,
        wrapTrigger: avatar => <CardTrigger variant="avatar" title="Admin" aside={abbreviateAddress(address_0)} label={`Admin ${abbreviateAddress(address_0)}, show the address`} rows={toRows([address_0])} card={<AdminAddressCard chainId={chainId} address={address_0} />}>{avatar}</CardTrigger>
      });
      $[8] = chainId;
      $[9] = toRows;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    t3 = admins.map(t4);
    $[4] = admins;
    $[5] = chainId;
    $[6] = toRows;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const items = t3;
  let t4;
  if ($[11] !== hidden || $[12] !== toRows) {
    t4 = marker => <CardTrigger variant="avatar" title="Admin" aside={`${hidden.length} more`} label={`${hidden.length} more admins, show their addresses`} rows={toRows(hidden)}>{marker}</CardTrigger>;
    $[11] = hidden;
    $[12] = toRows;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== align || $[15] !== items || $[16] !== t4) {
    t5 = <DynamicAvatarsStack items={items} maxTokensCount={MAX_VISIBLE_AVATARS} avatarSize="small" align={align} wrapExtra={t4} />;
    $[14] = align;
    $[15] = items;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
}
export function ProviderValue(t0) {
  const $ = _c(5);
  const {
    providers
  } = t0;
  if (!providers?.length) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Muted />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (providers.length === 1) {
    const [provider] = providers;
    if (provider) {
      let t1;
      if ($[1] !== provider) {
        t1 = <ProviderAttribute provider={provider} />;
        $[1] = provider;
        $[2] = t1;
      } else {
        t1 = $[2];
      }
      return t1;
    }
  }
  let t1;
  if ($[3] !== providers) {
    t1 = <ProviderStack providers={providers} />;
    $[3] = providers;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
function ProviderStack(t0) {
  const $ = _c(10);
  const {
    providers
  } = t0;
  let t1;
  if ($[0] !== providers) {
    t1 = providers.slice(MAX_VISIBLE_AVATARS);
    $[0] = providers;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hidden = t1;
  const toRows = _temp4;
  let t2;
  if ($[2] !== providers) {
    let t3;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = provider_0 => ({
        logoUrl: providerLogo(provider_0),
        wrapTrigger: avatar => <CardTrigger variant="avatar" title="Provider" aside={provider_0.name} label={`Provider ${provider_0.name}, show details`} rows={toRows([provider_0])} card={<div className="p-[16px]"><ProviderAttribute provider={provider_0} /></div>}>{avatar}</CardTrigger>
      });
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    t2 = providers.map(t3);
    $[2] = providers;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const items_0 = t2;
  let t3;
  if ($[5] !== hidden) {
    t3 = marker => <CardTrigger variant="avatar" title="Providers" aside={`${hidden.length} more`} label={`${hidden.length} more providers, show them`} rows={toRows(hidden)}>{marker}</CardTrigger>;
    $[5] = hidden;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== items_0 || $[8] !== t3) {
    t4 = <DynamicAvatarsStack items={items_0} maxTokensCount={MAX_VISIBLE_AVATARS} avatarSize="small" align="right" rounded={true} wrapExtra={t3} />;
    $[7] = items_0;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}

/** The curator hover card's header, for one admin: avatar and short address, then copy and ↗. */
function _temp4(items) {
  return items.map(_temp3);
}
function _temp3(provider) {
  return <div key={provider.id} className="gap-sm px-sm py-xs flex w-full items-center justify-between"><ProviderAttribute provider={provider} /></div>;
}
function AdminAddressCard(t0) {
  const $ = _c(15);
  const {
    chainId,
    address
  } = t0;
  let t1;
  if ($[0] !== address) {
    t1 = <Avatar address={address} size="medium" aria-hidden={true} />;
    $[0] = address;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== address) {
    t2 = abbreviateAddress(address);
    $[2] = address;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <span className="text-foreground text-xs whitespace-nowrap">{t2}</span>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t3) {
    t4 = <span className="gap-2xs flex min-w-0 items-center">{t1}{t3}</span>;
    $[6] = t1;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== address || $[10] !== chainId) {
    t5 = <AddressActions chainId={chainId} address={address} />;
    $[9] = address;
    $[10] = chainId;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <div className="gap-sm flex items-center justify-between p-[16px]">{t4}{t5}</div>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}

/** A Value secured card row for an admin: avatar and short address, then copy and ↗. */
function AdminCardRow(t0) {
  const $ = _c(15);
  const {
    chainId,
    address
  } = t0;
  let t1;
  if ($[0] !== address) {
    t1 = <Avatar address={address} size="small" aria-hidden={true} />;
    $[0] = address;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== address) {
    t2 = abbreviateAddress(address);
    $[2] = address;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <span className="text-2xs text-foreground whitespace-nowrap">{t2}</span>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t3) {
    t4 = <span className="gap-xs flex min-w-0 items-center">{t1}{t3}</span>;
    $[6] = t1;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== address || $[10] !== chainId) {
    t5 = <AddressActions chainId={chainId} address={address} className="-my-2xs" />;
    $[9] = address;
    $[10] = chainId;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <div className="gap-sm px-xs py-2xs flex w-full items-center justify-between">{t4}{t5}</div>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
function AddressActions(t0) {
  const $ = _c(14);
  const {
    chainId,
    address,
    className
  } = t0;
  let t1;
  if ($[0] !== address || $[1] !== chainId) {
    t1 = ChainUtils.getExplorerAddressUrl(chainId, address);
    $[0] = address;
    $[1] = chainId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const href = t1;
  let t2;
  if ($[3] !== className) {
    t2 = cn("flex shrink-0 items-center", className);
    $[3] = className;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== address) {
    t3 = <ClipboardButton size="sm" toCopy={address} toastMessage="Address copied to clipboard." tooltipMessage="Copy address to clipboard." />;
    $[5] = address;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== address || $[8] !== href) {
    t4 = href && <ButtonLink href={href} className="flex" buttonProps={{
      variant: "ghost",
      size: "sm",
      iconRight: undefined,
      iconAlone: <><ArrowDiagonalUp className="text-secondary-foreground" /><span className="sr-only">{`View ${abbreviateAddress(address)} on the explorer`}</span></>
    }} />;
    $[7] = address;
    $[8] = href;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
    t5 = <span className={t2}>{t3}{t4}</span>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}
const CLOSE_DELAY_MS = 150;

/** The curator hover card's layout: a header row with the title and its total, a divider, then the rows. */
function CardLayout(t0) {
  const $ = _c(15);
  const {
    title,
    aside,
    rows
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <span className="text-foreground text-xs">{title}</span>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== aside) {
    t2 = <span className="text-2xs text-foreground whitespace-nowrap">{aside}</span>;
    $[2] = aside;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="px-xs py-2xs flex w-full items-center justify-between">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Separator />;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3) {
    t5 = <div className="gap-xs px-sm pt-sm flex flex-col">{t3}{t4}</div>;
    $[8] = t3;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== rows) {
    t6 = <CardRows rows={rows} />;
    $[10] = rows;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t5 || $[13] !== t6) {
    t7 = <div className="gap-2xs flex w-full flex-col">{t5}{t6}</div>;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}
function CardRows(t0) {
  const $ = _c(2);
  const {
    rows
  } = t0;
  let t1;
  if ($[0] !== rows) {
    t1 = <div className="p-sm flex max-h-[200px] flex-col overflow-auto">{rows}</div>;
    $[0] = rows;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Dotted-underlined text, or an avatar, opening a card of rows. Hover or focus opens it, and it stays
 * open while the pointer or focus moves into it. A click, Enter or Space pins it; Esc, an outside click
 * or a second click closes it. Touch screens open a bottom sheet with the same rows.
 */
export function CardTrigger(t0) {
  const $ = _c(61);
  const {
    title,
    aside,
    label,
    rows,
    variant: t1,
    card,
    className,
    children
  } = t0;
  const variant = t1 === undefined ? "text" : t1;
  const touch = useMediaQuery(TOUCH_QUERY);
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const triggerRef = useRef(null);
  const contentRef = useRef(null);
  const closeTimer = useRef(undefined);
  const returningFocus = useRef(false);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      clearTimeout(closeTimer.current);
      setHovered(true);
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const show = t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      clearTimeout(closeTimer.current);
      closeTimer.current = setTimeout(() => setHovered(false), CLOSE_DELAY_MS);
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const hide = t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      clearTimeout(closeTimer.current);
      setHovered(false);
      setPinned(false);
    };
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const close = t4;
  const open = hovered || pinned;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => [...(contentRef.current?.querySelectorAll(FOCUSABLE) ?? [])];
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const focusables = t5;
  let t6;
  if ($[4] !== open || $[5] !== pinned || $[6] !== touch) {
    t6 = event => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        if (touch) {
          setPinned(true);
        } else {
          if (pinned) {
            close();
          } else {
            setPinned(true);
          }
        }
      } else {
        if (event.key === "Tab" && !event.shiftKey && open) {
          const [first] = focusables();
          if (!first) {
            return;
          }
          event.preventDefault();
          first.focus();
        }
      }
    };
    $[4] = open;
    $[5] = pinned;
    $[6] = touch;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const onTriggerKeyDown = t6;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = event_0 => {
      if (event_0.key !== "Tab") {
        return;
      }
      const items = focusables();
      const edge = event_0.shiftKey ? items[0] : items.at(-1);
      if (document.activeElement !== edge) {
        return;
      }
      event_0.preventDefault();
      triggerRef.current?.focus();
      if (!event_0.shiftKey) {
        close();
      }
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const onContentKeyDown = t7;
  const avatar = variant === "avatar";
  const underline = avatar ? "flex cursor-pointer rounded-full" : "decoration-secondary-foreground cursor-pointer underline decoration-dotted underline-offset-[3px]";
  if (touch) {
    let t8;
    if ($[9] !== className || $[10] !== underline) {
      t8 = cn(underline, className);
      $[9] = className;
      $[10] = underline;
      $[11] = t8;
    } else {
      t8 = $[11];
    }
    let t9;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = event_1 => {
        event_1.stopPropagation();
        setPinned(true);
      };
      $[12] = t9;
    } else {
      t9 = $[12];
    }
    let t10;
    if ($[13] !== children || $[14] !== label || $[15] !== onTriggerKeyDown || $[16] !== t8) {
      t10 = <span role="button" tabIndex={0} aria-label={label} className={t8} onClick={t9} onKeyDown={onTriggerKeyDown}>{children}</span>;
      $[13] = children;
      $[14] = label;
      $[15] = onTriggerKeyDown;
      $[16] = t8;
      $[17] = t10;
    } else {
      t10 = $[17];
    }
    let t11;
    if ($[18] !== rows) {
      t11 = <CardRows rows={rows} />;
      $[18] = rows;
      $[19] = t11;
    } else {
      t11 = $[19];
    }
    let t12;
    if ($[20] !== aside || $[21] !== pinned || $[22] !== t11 || $[23] !== title) {
      t12 = <BottomSheet open={pinned} onOpenChange={setPinned} title={title} aside={aside}>{t11}</BottomSheet>;
      $[20] = aside;
      $[21] = pinned;
      $[22] = t11;
      $[23] = title;
      $[24] = t12;
    } else {
      t12 = $[24];
    }
    let t13;
    if ($[25] !== t10 || $[26] !== t12) {
      t13 = <>{t10}{t12}</>;
      $[25] = t10;
      $[26] = t12;
      $[27] = t13;
    } else {
      t13 = $[27];
    }
    return t13;
  }
  let t8;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = next => !next && close();
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== className || $[30] !== underline) {
    t9 = cn(underline, className);
    $[29] = className;
    $[30] = underline;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      if (returningFocus.current) {
        returningFocus.current = false;
      } else {
        show();
      }
    };
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== pinned) {
    t11 = () => pinned ? close() : setPinned(true);
    $[33] = pinned;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== children || $[36] !== label || $[37] !== onTriggerKeyDown || $[38] !== open || $[39] !== t11 || $[40] !== t9) {
    t12 = <PopoverAnchor asChild={true}><span ref={triggerRef} role="button" tabIndex={0} aria-label={label} aria-expanded={open} className={t9} onPointerEnter={show} onPointerLeave={hide} onFocus={t10} onBlur={hide} onClick={t11} onKeyDown={onTriggerKeyDown}>{children}</span></PopoverAnchor>;
    $[35] = children;
    $[36] = label;
    $[37] = onTriggerKeyDown;
    $[38] = open;
    $[39] = t11;
    $[40] = t9;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  const t13 = avatar ? "top" : "bottom";
  const t14 = avatar ? "center" : "end";
  const t15 = avatar ? 8 : 4;
  let t16;
  if ($[42] !== avatar) {
    t16 = avatar ? {
      minWidth: 275,
      maxWidth: 315
    } : {
      minWidth: 300,
      maxWidth: 400
    };
    $[42] = avatar;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  let t18;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => {
      if (!contentRef.current?.contains(document.activeElement)) {
        return;
      }
      returningFocus.current = true;
      triggerRef.current?.focus();
    };
    t18 = event_4 => {
      if (event_4.target instanceof Node && triggerRef.current?.contains(event_4.target)) {
        event_4.preventDefault();
      }
    };
    $[44] = t17;
    $[45] = t18;
  } else {
    t17 = $[44];
    t18 = $[45];
  }
  let t19;
  if ($[46] !== aside || $[47] !== card || $[48] !== rows || $[49] !== title) {
    t19 = card ?? <CardLayout title={title} aside={aside} rows={rows} />;
    $[46] = aside;
    $[47] = card;
    $[48] = rows;
    $[49] = title;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  let t20;
  if ($[51] !== t13 || $[52] !== t14 || $[53] !== t15 || $[54] !== t16 || $[55] !== t19) {
    t20 = <PopoverContent ref={contentRef} side={t13} align={t14} sideOffset={t15} className="w-max rounded-[20px] p-0" style={t16} onOpenAutoFocus={_temp5} onCloseAutoFocus={_temp6} onEscapeKeyDown={t17} onInteractOutside={t18} onPointerEnter={show} onPointerLeave={hide} onFocus={show} onBlur={hide} onKeyDown={onContentKeyDown}>{t19}</PopoverContent>;
    $[51] = t13;
    $[52] = t14;
    $[53] = t15;
    $[54] = t16;
    $[55] = t19;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  let t21;
  if ($[57] !== open || $[58] !== t12 || $[59] !== t20) {
    t21 = <Popover open={open} onOpenChange={t8}>{t12}{t20}</Popover>;
    $[57] = open;
    $[58] = t12;
    $[59] = t20;
    $[60] = t21;
  } else {
    t21 = $[60];
  }
  return t21;
}

/** ├ or └ guide in front of a child row, spanning its full height; position it with `className`. */
function _temp6(event_3) {
  return event_3.preventDefault();
}
function _temp5(event_2) {
  return event_2.preventDefault();
}
export function TreeConnector(t0) {
  const $ = _c(10);
  const {
    last,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("pointer-events-none absolute inset-y-0", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = last ? "h-1/2" : "h-full";
  let t3;
  if ($[2] !== t2) {
    t3 = cn("border-border absolute top-0 left-0 border-l-[0.5px]", t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <span className={t3} />;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="border-border absolute top-1/2 left-0 w-full border-t-[0.5px]" />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t1 || $[8] !== t4) {
    t6 = <span aria-hidden={true} className={t1}>{t4}{t5}</span>;
    $[7] = t1;
    $[8] = t4;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}
type TipTerm = {
  label: string;
  value: string;
};
const maturityDate = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC'
});
const discountPercentage = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2
});
function discountTerms(terms: FeedRow['terms']): TipTerm[] {
  if (!terms) return [];
  const hints: TipTerm[] = [];
  if (terms.baseDiscountPerYear !== null) {
    const discount = Number(terms.baseDiscountPerYear);
    if (Number.isFinite(discount)) {
      hints.push({
        label: 'Discount',
        value: `${discountPercentage.format(discount * 100)}%/yr`
      });
    }
  }
  if (terms.maturity !== null) {
    hints.push({
      label: 'Matures',
      value: maturityDate.format(terms.maturity * 1000)
    });
  }
  return hints;
}
function TipLine(t0) {
  const $ = _c(7);
  const {
    terms,
    stale
  } = t0;
  let t1;
  if ($[0] !== stale) {
    t1 = stale && <span className="text-warning-foreground">Stale · </span>;
    $[0] = stale;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== terms) {
    t2 = terms.map(_temp7);
    $[2] = terms;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <span className="block whitespace-nowrap">{t1}{t2}</span>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}

/** The latest price, with a hint of its last update and staleness; stale feeds also get a warning icon. */
function _temp7(t0, index) {
  const {
    label,
    value
  } = t0;
  return <Fragment key={label}>{index > 0 && " \xB7 "}<span className="text-secondary-foreground">{label}</span> {value}</Fragment>;
}
export function FeedValue(t0) {
  const $ = _c(36);
  const {
    row,
    className
  } = t0;
  const age = row.age;
  if (row.price === null) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Muted />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== row.price) {
    t1 = formatFeedValue(row.price);
    $[1] = row.price;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const value = t1;
  let lines;
  let stale;
  let t2;
  let t3;
  if ($[3] !== age || $[4] !== className || $[5] !== row || $[6] !== value) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb0: {
      stale = isStale(row);
      lines = [];
      if (age !== null) {
        let t4;
        if ($[11] !== age) {
          t4 = formatAge(age);
          $[11] = age;
          $[12] = t4;
        } else {
          t4 = $[12];
        }
        const terms = [{
          label: "Last price update",
          value: `${t4} ago`
        }];
        if (stale && row.heartbeat !== null) {
          terms.push({
            label: "Heartbeat",
            value: formatDuration(row.heartbeat)
          });
        }
        lines.push({
          terms,
          stale
        });
      }
      const terms_0 = discountTerms(row.terms);
      if (terms_0.length > 0) {
        lines.push({
          terms: terms_0
        });
      }
      if (lines.length === 0) {
        let t4;
        if ($[13] !== className) {
          t4 = cn("tabular-nums", className);
          $[13] = className;
          $[14] = t4;
        } else {
          t4 = $[14];
        }
        let t5;
        if ($[15] !== t4 || $[16] !== value) {
          t5 = <span className={t4}>{value}</span>;
          $[15] = t4;
          $[16] = value;
          $[17] = t5;
        } else {
          t5 = $[17];
        }
        t3 = t5;
        break bb0;
      }
      let t4;
      if ($[18] !== value) {
        t4 = [value];
        $[18] = value;
        $[19] = t4;
      } else {
        t4 = $[19];
      }
      t2 = t4.concat(lines.map(_temp9));
    }
    $[3] = age;
    $[4] = className;
    $[5] = row;
    $[6] = value;
    $[7] = lines;
    $[8] = stale;
    $[9] = t2;
    $[10] = t3;
  } else {
    lines = $[7];
    stale = $[8];
    t2 = $[9];
    t3 = $[10];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  const label = t2.join(" \xB7 ");
  let t4;
  if ($[20] !== lines) {
    t4 = <span className="text-foreground gap-2xs flex flex-col">{lines.map(_temp10)}</span>;
    $[20] = lines;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  let t5;
  if ($[22] !== className) {
    t5 = cn("decoration-secondary-foreground tabular-nums underline decoration-dotted underline-offset-[3px]", className);
    $[22] = className;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  let t6;
  if ($[24] !== t5 || $[25] !== value) {
    t6 = <span className={t5}>{value}</span>;
    $[24] = t5;
    $[25] = value;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  let t7;
  if ($[27] !== stale) {
    t7 = stale && <WarningTriangleSolid aria-hidden={true} className="text-warning-foreground size-3.5 shrink-0" />;
    $[27] = stale;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  let t8;
  if ($[29] !== t6 || $[30] !== t7) {
    t8 = <span className="gap-2xs inline-flex items-center">{t6}{t7}</span>;
    $[29] = t6;
    $[30] = t7;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  let t9;
  if ($[32] !== label || $[33] !== t4 || $[34] !== t8) {
    t9 = <Hint title="Latest price" content={t4} label={label} underline={false}>{t8}</Hint>;
    $[32] = label;
    $[33] = t4;
    $[34] = t8;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  return t9;
}

/** The filled warning icon a stale feed gets after its time and latest value. */
function _temp10(line_0, index) {
  return <TipLine key={index} terms={line_0.terms} stale={line_0.stale} />;
}
function _temp9(line) {
  return [line.stale ? "Stale" : null, ...line.terms.map(_temp8)].filter(Boolean).join(" \xB7 ");
}
function _temp8(term) {
  return `${term.label} ${term.value}`;
}
export function StaleMark(t0) {
  const $ = _c(1);
  const {
    row
  } = t0;
  if (!isStale(row)) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <WarningTriangleSolid aria-hidden={true} className="text-warning-foreground size-3.5 shrink-0" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  return t1;
}

/** Muted `Collateral ·` then `[logo] wstETH`, or just `Collateral` when the asset is unknown. */
export function SideLabel(t0) {
  const $ = _c(10);
  const {
    group
  } = t0;
  if (!group.symbol) {
    let t1;
    if ($[0] !== group.label) {
      t1 = <span className="text-muted-foreground">{group.label}</span>;
      $[0] = group.label;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  const t1 = SIDE_LABEL[group.side];
  let t2;
  if ($[2] !== t1) {
    t2 = <span className="text-muted-foreground">{t1} ·</span>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== group.logo) {
    t3 = <Avatar src={group.logo} size={16} padding={0} rounded={true} aria-hidden={true} />;
    $[4] = group.logo;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== group.symbol || $[7] !== t2 || $[8] !== t3) {
    t4 = <span className="gap-2xs inline-flex items-center">{t2}{t3}{group.symbol}</span>;
    $[6] = group.symbol;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}

/** `12m ago`, or `3h ago ⚠` once stale; hovering a stale one shows Stale, its age and heartbeat. */
export function LastUpdateValue(t0) {
  const $ = _c(21);
  const {
    row
  } = t0;
  const age = row.age;
  if (age === null) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Muted />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== age) {
    t1 = formatAge(age);
    $[1] = age;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <span className="tabular-nums">{t1} ago</span>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const time = t2;
  if (!isStale(row) || row.heartbeat === null) {
    return time;
  }
  let t3;
  if ($[5] !== age) {
    t3 = formatAge(age);
    $[5] = age;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== row.heartbeat) {
    t4 = formatDuration(row.heartbeat);
    $[7] = row.heartbeat;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const facts = `Last price update ${t3} ago · Heartbeat ${t4}`;
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="text-warning-foreground">Stale</span>;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== facts) {
    t6 = <>{t5} · {facts}</>;
    $[10] = facts;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = `Stale · ${facts}`;
  let t8;
  if ($[12] !== row) {
    t8 = <StaleMark row={row} />;
    $[12] = row;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t8 || $[15] !== time) {
    t9 = <span className="gap-2xs inline-flex items-center">{time}{t8}</span>;
    $[14] = t8;
    $[15] = time;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t6 || $[18] !== t7 || $[19] !== t9) {
    t10 = <Hint title="Last price update" content={t6} label={t7} underline={false}>{t9}</Hint>;
    $[17] = t6;
    $[18] = t7;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  return t10;
}
export function UpdatesValue(t0) {
  const $ = _c(5);
  const {
    row
  } = t0;
  let t1;
  if ($[0] !== row) {
    t1 = formatUpdates(row);
    $[0] = row;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const updates = t1;
  if (updates === EMPTY) {
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Muted />;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  let t2;
  if ($[3] !== updates) {
    t2 = <span className="tabular-nums">{updates}</span>;
    $[3] = updates;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

/** The feed type as reported by its provider or classified by Morpho's oracle decoder. */
export function TypeBadge(t0) {
  const $ = _c(9);
  const {
    type,
    providerType
  } = t0;
  let t1;
  if ($[0] !== providerType || $[1] !== type) {
    t1 = typeBadgeLabel({
      type,
      providerType
    });
    $[0] = providerType;
    $[1] = type;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const label = t1;
  if (label === null) {
    let t2;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Muted />;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  const content = providerType ? `Type as reported by ${providerType.providerName}` : "Type as classified by Morpho's oracle decoder";
  let t2;
  if ($[4] !== label) {
    t2 = <Tag variant="main" label={label} />;
    $[4] = label;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== content || $[7] !== t2) {
    t3 = <Hint title="Type" underline={false} content={content}>{t2}</Hint>;
    $[6] = content;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}